<template>
  <div>
    <!-- Select Plan -->
    <div class="mb-6">
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {{ hasSubscription ? t('app.sysadmin.modal.change_plan') : t('app.sysadmin.modal.select_plan') }}
      </label>
      <select
        :value="selectedPlanId"
        class="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
        @change="$emit('update:selectedPlanId', Number(($event.target as HTMLSelectElement).value))"
      >
        <option :value="null">
          {{ t('app.sysadmin.modal.select_plan_placeholder') }}
        </option>
        <option
          v-for="plan in plans"
          :key="plan.id"
          :value="plan.id"
        >
          {{ plan.display_name || plan.name }} - ${{ plan.monthly_price }}/{{ t('app.common.month') }}
        </option>
      </select>
    </div>

    <!-- Plan Details (if selected) -->
    <div
      v-if="selectedPlan"
      class="mb-6 p-4 border border-gray-200 dark:border-gray-600 rounded-lg"
    >
      <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">
        {{ selectedPlan.display_name }}
      </h4>
      <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">
        {{ selectedPlan.description }}
      </p>
        
      <div class="grid grid-cols-2 gap-4 text-sm mb-4">
        <div>
          <span class="text-gray-500 dark:text-gray-400">{{ t('app.sysadmin.modal.limits.users') }}:</span>
          <span class="ml-2 text-gray-900 dark:text-white">
            {{ (selectedPlan.limits?.max_admin_users || 0) + (selectedPlan.limits?.max_waiter_users || 0) + (selectedPlan.limits?.max_cashier_users || 0) + (selectedPlan.limits?.max_kitchen_users || 0) }}
          </span>
        </div>
        <div>
          <span class="text-gray-500 dark:text-gray-400">{{ t('app.sysadmin.modal.limits.tables') }}:</span>
          <span class="ml-2 text-gray-900 dark:text-white">{{ selectedPlan.limits?.max_tables || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-500 dark:text-gray-400">{{ t('app.sysadmin.modal.limits.products') }}:</span>
          <span class="ml-2 text-gray-900 dark:text-white">{{ selectedPlan.limits?.max_menu_items || '-' }}</span>
        </div>
        <div>
          <span class="text-gray-500 dark:text-gray-400">{{ t('app.sysadmin.modal.price') }}:</span>
          <span class="ml-2 font-bold text-indigo-600 dark:text-indigo-400">${{ selectedPlan.monthly_price || 0 }}/{{ t('app.common.month') }}</span>
        </div>
      </div>

      <div class="flex flex-wrap gap-2">
        <span
          v-if="selectedPlan.features?.has_kitchen_module"
          class="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded"
        >
          {{ t('app.sysadmin.modal.features.kitchen') }}
        </span>
        <span
          v-if="selectedPlan.features?.has_ingredients_module"
          class="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded"
        >
          {{ t('app.sysadmin.modal.features.ingredients') }}
        </span>
        <span
          v-if="selectedPlan.features?.has_inventory_module"
          class="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded"
        >
          {{ t('app.sysadmin.modal.features.inventory') }}
        </span>
        <span
          v-if="selectedPlan.features?.has_advanced_reports"
          class="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded"
        >
          {{ t('app.sysadmin.modal.features.reports') }}
        </span>
      </div>
    </div>

    <!-- Billing Cycle (only for new subscriptions) -->
    <div
      v-if="!hasSubscription"
      class="mb-6"
    >
      <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {{ t('app.sysadmin.modal.billing_cycle') }}
      </label>
      <div class="flex gap-4">
        <label class="flex items-center">
          <input
            type="radio"
            name="billing-cycle"
            value="monthly"
            :checked="billingCycle === 'monthly'"
            class="mr-2"
            @change="$emit('update:billingCycle', 'monthly')"
          >
          <span class="text-sm text-gray-900 dark:text-white">{{ t('app.sysadmin.modal.monthly') }}</span>
        </label>
        <label class="flex items-center">
          <input
            type="radio"
            name="billing-cycle"
            value="annual"
            :checked="billingCycle === 'annual'"
            class="mr-2"
            @change="$emit('update:billingCycle', 'annual')"
          >
          <span class="text-sm text-gray-900 dark:text-white">{{ t('app.sysadmin.modal.annual') }} (25% {{ t('app.sysadmin.modal.discount') }})</span>
        </label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

interface Plan {
  id: number;
  name: string;
  display_name?: string;
  description: string;
  monthly_price: number;
  limits?: {
    max_admin_users?: number;
    max_waiter_users?: number;
    max_cashier_users?: number;
    max_kitchen_users?: number;
    max_tables?: number;
    max_menu_items?: number;
  };
  features?: {
    has_kitchen_module?: boolean;
    has_ingredients_module?: boolean;
    has_inventory_module?: boolean;
    has_advanced_reports?: boolean;
  };
}

const { t } = useI18n();

defineProps<{
  hasSubscription: boolean;
  plans: Plan[];
  selectedPlanId: number | null;
  selectedPlan: Plan | null;
  billingCycle: 'monthly' | 'annual';
}>();

defineEmits<{
  (e: 'update:selectedPlanId', value: number | null): void;
  (e: 'update:billingCycle', value: 'monthly' | 'annual'): void;
}>();
</script>
